import React, { useState } from 'react';
import {
    Input,
    Dropdown,
    Option,
    Label,
    makeStyles,
    useId,
    Title3,
    Checkbox,
    CheckboxProps,
} from '@fluentui/react-components';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, changeSetting } from '../../utils/store';
import { MsalAuthenticationTemplate } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';

const useStyles = makeStyles({
    root: {
        display: 'grid',
        gridTemplateRows: 'repeat(1fr)',
        justifyItems: 'start',
        gap: '20px',
        maxWidth: '400px',
        padding: '32px',
    },
    field: {
        display: 'grid',
        justifyItems: 'start',
        gap: '2px',
    },
    'root a': {
        color: '#74C7FF',
        textDecoration: 'underline',
        '&:hover': {
            color: '#9DD9FF',
        },
        '&:active': {
            color: '#4DB6FF',
        },
    },
});

const endpointOptions = [
    { text: 'Prod', value: 'https://wa-orchestration.azurewebsites.net' },
    {
        text: 'Staging',
        value: 'https://wa-orchestration-staging.azurewebsites.net',
    },
    {
        text: 'Duke (Beta)',
        value: 'https://wa-orchestration.azurewebsites.net',
    },
    { text: 'Local', value: 'https://localhost:7147' },
    { text: 'Custom', value: 'Custom' },
];

const Settings: React.FC = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const dropdownId = useId('dropdown');

    const globalState = useSelector((state: RootState) => state.settings);
    const authRequest = { ...loginRequest };

    // Find the option that matches the endpoint from the store
    const selectedOption = endpointOptions.find(
        (option) => option.value === globalState.apiEndpoint
    );

    // If a matching option is found, use its text. Otherwise, default to 'Custom'
    const selectedOptionText = selectedOption ? selectedOption.text : 'Custom';

    const [endpointSelection, setEndpointSelection] = useState(
        globalState.apiEndpoint ?? 'https://wa-orchestration.azurewebsites.net'
    );

    const handleEndpointChange = (event: any, option: any) => {
        const value = option.optionValue as string;
        setEndpointSelection(value);
        dispatch(changeSetting({ key: 'apiEndpoint', value: value }));
    };

    const handleChatVersionChange = (
        ev: React.FormEvent<HTMLElement>,
        data: CheckboxProps
    ) => {
        const isChecked = data.checked === true;
        dispatch(changeSetting({ key: 'useChatv2', value: isChecked }));
    };

    const handleExportFeatureChange = (
        ev: React.FormEvent<HTMLElement>,
        data: CheckboxProps
    ) => {
        const isChecked = data.checked === true;
        dispatch(changeSetting({ key: 'enableExportFeature', value: isChecked }));
    };

    const handleEvaluationChange = (
        ev: React.FormEvent<HTMLElement>,
        data: CheckboxProps
    ) => {
        const isChecked = data.checked === true;
        dispatch(changeSetting({ key: 'enableEvaluation', value: isChecked }));
    };

    return (
        <MsalAuthenticationTemplate
            interactionType={InteractionType.Redirect}
            authenticationRequest={authRequest}
        >
            <div>
                <div className={classes.root}>
                    <Title3>Settings</Title3>
                    <div className={classes.field}>
                        <Label id={dropdownId}>API Endpoint</Label>
                        <Dropdown
                            aria-labelledby={dropdownId}
                            placeholder="Select API Endpoint"
                            onOptionSelect={handleEndpointChange}
                            defaultValue={selectedOptionText}
                            defaultSelectedOptions={[selectedOptionText]}
                        >
                            {endpointOptions.map((option, index) => (
                                <Option value={option.value} text={option.text} key={index}>
                                    {option.text}
                                </Option>
                            ))}
                        </Dropdown>
                    </div>
                    <div className={classes.field}>
                        <Input
                            placeholder="Custom API Endpoint"
                            value={globalState.apiEndpoint}
                            disabled={selectedOptionText !== 'Custom'}
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                            ) =>
                                dispatch(
                                    changeSetting({
                                        key: 'apiEndpoint',
                                        value: event.target.value,
                                    })
                                )
                            }
                            style={{ width: '500px' }}
                        />
                    </div>
                    <div className={classes.field}>
                        <Checkbox
                            label="Use chat v2"
                            checked={globalState.useChatv2}
                            onChange={handleChatVersionChange}
                        />
                    </div>
                    <div className={classes.field}>
                        <Checkbox
                            label="Enable export dataset feature"
                            checked={globalState.enableExportFeature}
                            onChange={handleExportFeatureChange}
                        />
                    </div>
                    <div className={classes.field}>
                        <Checkbox
                            label="Enable evaluation"
                            checked={globalState.enableEvaluation}
                            onChange={handleEvaluationChange}
                        />
                    </div>
                </div>
            </div>
        </MsalAuthenticationTemplate>
    );
};

export default Settings;
